import { useCallback, useContext, useEffect, useRef } from "react";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import {
  accessTokenState,
  actionState,
  askUserState,
  chatSettingsInputsState,
  chatSettingsValueState,
  currentFileRefIdState,
  currentThreadIdState,
  elementState,
  firstUserInteraction,
  loadingState,
  messagesState,
  // reportState,
  sessionIdState,
  sessionState,
  sideViewState,
  tasklistState,
  threadIdToResumeState,
  tokenCountState,
  chatTypeState,
} from "./state";
import { IAction, IFileRef, IStep } from "./types";
import { addMessage, updateMessageById } from "./utils/message";
import { v4 as uuidv4 } from "uuid";

import { GenAiContext } from "./context";
import { apiClient } from "@/api";
import { DEFAULT_CONFIG } from "@/utils/config";
import { isArray } from "lodash";

// type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

const useChatInteract = () => {
  const client = useContext(GenAiContext);
  const accessToken = useRecoilValue(accessTokenState);
  const session = useRecoilValue(sessionState);
  const sessionId = useRecoilValue(sessionIdState);

  const [askUser, setAskUser] = useRecoilState(askUserState);
  const [currentFileRefId, setCurrentFileRefId] = useRecoilState(
    currentFileRefIdState
  );

  const chatType = useRecoilValue(chatTypeState);
  const chatTypeRef = useRef(chatType);

  useEffect(() => {
    chatTypeRef.current = chatType;
  }, [chatType]);

  const resetChatSettings = useResetRecoilState(chatSettingsInputsState);
  const resetSessionId = useResetRecoilState(sessionIdState);
  const resetChatSettingsValue = useResetRecoilState(chatSettingsValueState);

  const setFirstUserInteraction = useSetRecoilState(firstUserInteraction);
  const setLoading = useSetRecoilState(loadingState);
  const setMessages = useSetRecoilState(messagesState);
  // const setReport = useSetRecoilState(reportState);
  const setElements = useSetRecoilState(elementState);
  const setTasklists = useSetRecoilState(tasklistState);
  const setActions = useSetRecoilState(actionState);
  const setTokenCount = useSetRecoilState(tokenCountState);
  const setIdToResume = useSetRecoilState(threadIdToResumeState);
  const setSideView = useSetRecoilState(sideViewState);
  const setCurrentThreadId = useSetRecoilState(currentThreadIdState);
  const threadId = useRecoilValue(currentThreadIdState);
  const threadIdRef = useRef(threadId);

  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  const clear = useCallback(() => {
    setIdToResume(undefined);
    resetSessionId();
    setFirstUserInteraction(undefined);
    setMessages([]);
    setElements([]);
    setTasklists([]);
    setActions([]);
    setTokenCount(0);
    resetChatSettings();
    resetChatSettingsValue();
    setSideView(undefined);
    setCurrentThreadId(undefined);
    setCurrentFileRefId(undefined);
    setAskUser(undefined);
  }, []);

  const sendMessage = useCallback(
    (message: IStep, company_id?: string | number, fileRef?: number | null) => {
      setLoading(true);

      const fileRefId = fileRef || currentFileRefId;

      message.role = "user";

      if (!message.id) {
        message.id = uuidv4();
      }

      if (!message.timestamp) {
        message.timestamp = new Date().toISOString();
      }

      // if (!threadId) {
      //   setCurrentThreadId(message.id);
      // }

      setMessages((oldMessages) => addMessage(oldMessages, message as IStep));

      apiClient
        .sendMessage(
          message.content,
          threadIdRef.current ?? "",
          chatTypeRef.current,
          fileRefId,
          accessToken,
          undefined,
          company_id
        )
        .then((response) => {
          message.steps = [];

          if (response.data.steps) {
            const steps = response.data.steps;
            const latest = steps.length - 1;

            if (steps[latest]) {
              const step = { ...steps[latest] };
              message.steps.push(step);
              setCurrentThreadId(response.data.thread_id);
            }
          }

          setMessages((oldMessages) =>
            updateMessageById(
              oldMessages,
              message.id as string,
              message as IStep
            )
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  const sendReport = useCallback(() =>
    // message: PartialBy<IStep, "createdAt" | "id">,
    // company_id?: string,
    // fileRef?: number | null
    {
      // setLoading(true);
      // const fileRefId = fileRef || currentFileRefId;
      // if (!message.id) {
      //   message.id = uuidv4();
      // }
      // if (!message.threadId) {
      //   message.threadId = "";
      // }
      // if (!message.createdAt) {
      //   message.createdAt = new Date().toISOString();
      // }
      // setMessages((oldMessages) => addMessage(oldMessages, message as IStep));
      // apiClient
      //   .sendMessage(
      //     message.content,
      //     message.threadId,
      //     fileRefId,
      //     accessToken,
      //     undefined,
      //     company_id
      //   )
      //   .then((response) => {
      //     const latestStep =
      //       response.data.steps[response.data.steps.length - 1];
      //     // Get the content of the latest step
      //     const latestcontent = lateststep.content;
      //     setCurrentThreadId(response.data.threadId);
      //     // Set the report with the latest content
      //     setReport(latestcontent);
      //   })
      //   .finally(() => {
      //     setLoading(false);
      //   });
    }, []);

  const sendAskFileMessage = useCallback(() => {
    const uploadConfig = DEFAULT_CONFIG.features.spontaneous_file_upload;

    const ask = {
      createdAt: new Date().toISOString(),
      id: uuidv4(),
      name: "All Data International AI",
      content: "Selamat datang, silahkan upload file anda",
      role: "assistant",
      waitForAnswer: true,
    };

    setMessages((oldMessages) => addMessage(oldMessages, ask as IStep));

    setAskUser({
      spec: {
        type: "file",
        timeout: 5000,
        max_files: uploadConfig.max_files,
        // max_size_mb: uploadConfig.max_size_mb,
        accept: uploadConfig.accept,
      },
      callback: (payload) => {
        ask.id = uuidv4();
        ask.createdAt = new Date().toISOString();
        ask.waitForAnswer = false;
        ask.content = "File berhasil diupload. Apa yang ingin anda tanyakan?";

        if (isArray(payload) && payload.length > 0) {
          if (payload[0].message) ask.content = payload[0].message;
          setCurrentFileRefId(payload[0].data);
        }

        setMessages((oldMessages) =>
          updateMessageById(oldMessages, ask.id as string, ask as IStep)
        );

        setAskUser(undefined);
      },
    });
  }, []);

  const sendFileUploadSuccessMessage = useCallback(
    (filename: string) => {
      const successMessage = {
        createdAt: new Date().toISOString(),
        id: uuidv4(),
        name: "All Data International AI",
        content: `Upload file berhasil: ${filename}`,
        role: "assistant",
        waitForAnswer: false,
      };

      setMessages((oldMessages) =>
        addMessage(oldMessages, successMessage as IStep)
      );
    },
    [setMessages]
  );

  const editMessage = useCallback(
    (message: IStep) => {
      session?.socket.emit("edit_message", { message });
    },
    [session?.socket]
  );

  const sendAudioChunk = useCallback(
    (isStart: boolean, mimeType: string, elapsedTime: number, data: Blob) => {
      session?.socket.emit("audio_chunk", {
        isStart,
        mimeType,
        elapsedTime,
        data,
      });
    },
    [session?.socket]
  );

  const endAudioStream = useCallback(
    (fileReferences?: IFileRef[]) => {
      session?.socket.emit("audio_end", { fileReferences });
    },
    [session?.socket]
  );

  const replyMessage = useCallback(
    (message: IStep) => {
      if (askUser) {
        setMessages((oldMessages) => addMessage(oldMessages, message));
        askUser.callback(message);
      }
    },
    [askUser]
  );

  const updateChatSettings = useCallback(
    (values: object) => {
      session?.socket.emit("chat_settings_change", values);
    },
    [session?.socket]
  );

  const stopTask = useCallback(() => {
    setMessages((oldMessages) =>
      oldMessages.map((m) => {
        m.streaming = false;
        return m;
      })
    );

    setLoading(false);

    session?.socket.emit("stop");
  }, [session?.socket]);

  const callAction = useCallback(
    (action: IAction) => {
      const socket = session?.socket;
      if (!socket) return;

      const promise = new Promise<{
        id: string;
        status: boolean;
        response?: string;
      }>((resolve, reject) => {
        socket.once("action_response", (response: any) => {
          if (response.status) {
            resolve(response);
          } else {
            reject(response);
          }
        });
      });

      socket.emit("action_call", action);

      return promise;
    },
    [session?.socket]
  );

  const uploadFile = useCallback(
    (file: File, onProgress: (progress: number) => void) => {
      return client.uploadFile(file, onProgress, sessionId, accessToken);
    },
    [sessionId, accessToken]
  );

  return {
    uploadFile,
    callAction,
    clear,
    replyMessage,
    sendMessage,
    sendReport,
    sendAskFileMessage,
    sendFileUploadSuccessMessage,
    editMessage,
    sendAudioChunk,
    endAudioStream,
    stopTask,
    setIdToResume,
    updateChatSettings,
  };
};

export { useChatInteract };
